import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { WarningCircle } from '@phosphor-icons/react';
import ScoreBadge from '../components/ScoreBadge';
import ScoreCell from '../components/ScoreCell';
import HoverCard from '../components/HoverCard';
import ScoreBreakdown from '../components/ScoreBreakdown';
import DischargeBreakdown from '../components/DischargeBreakdown';
import MonthRangePicker from '../components/MonthRangePicker';
import TrendPanel from '../components/TrendPanel';
import { api } from '../lib/api';
import { useScoring, isRedZone } from '../lib/scoring';
import { findMissingData, MISSING_FIELD_LABELS } from '../lib/missing-data';
import {
  LargeTitle, Section, Card, ListGroup, Monogram, ScorePill, SearchField, Segmented, ScoreHero,
  MonthChips, Switch, PageSkeleton, EmptyRow, tap,
} from '../components/ui';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * The in-progress month has a live caseload but no discharge or attendance data
 * yet, so it carries no CScore. Showing a red 0 there would read as a failing
 * clinician rather than a month that hasn't closed.
 */
function RecordScore({ record, weights, threshold }) {
  if (record.cscore == null) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full font-medium bg-gray-100 text-gray-500 min-w-[48px] h-7 text-xs px-2.5"
        title="Awaiting this month's discharge and attendance data"
      >
        Pending
      </span>
    );
  }
  return (
    <HoverCard panel={<ScoreBreakdown breakdown={record} score={record.cscore} weights={weights} title="This month" />}>
      <ScoreBadge score={record.cscore} flagged={isRedZone(record.cscore, threshold)} />
    </HoverCard>
  );
}

/**
 * The discharge count, with its type breakdown on hover. The number itself
 * stays editable — the hover explains what KIPU reported, the cell underneath
 * is still how a human corrects it.
 */
function DischargeCell({ record, clinicId, onRecordUpdate }) {
  return (
    <HoverCard panel={<DischargeBreakdown record={record} />}>
      <EditableCell value={record.discharges} field="discharges" recordId={record.id} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />
    </HoverCard>
  );
}

/**
 * Caseload is auto-calculated from the daily KIPU caseload report, so the cell
 * says where its number came from — and warns that typing over it pins the
 * value and stops future daily syncs from updating it.
 */
function CaseloadCell({ record, clinicId, onRecordUpdate }) {
  const daily = record.daily_caseload;
  const pinned = (record.manual_overrides || []).includes('caseload');

  const hint = pinned
    ? 'Manually set. Daily caseload syncs will no longer update this value.'
    : daily
      ? `Auto-calculated: ${daily.total} cases over ${daily.days} report day${daily.days === 1 ? '' : 's'} (${daily.first_date} to ${daily.last_date})`
      : null;

  return (
    <div className="inline-block" title={hint || undefined}>
      <EditableCell value={record.caseload} field="caseload" recordId={record.id} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />
      {pinned ? (
        <span className="block text-[10px] text-amber-600 px-2 leading-tight">Manual</span>
      ) : daily ? (
        <span className="block text-[10px] text-gray-400 px-2 leading-tight">{daily.total} / {daily.days}d</span>
      ) : null}
    </div>
  );
}

/**
 * Attendance is auto-calculated from the daily KIPU group attendance report,
 * so, like Caseload, the cell says where its number came from and warns that
 * typing over it pins the value.
 */
function AttendanceCell({ record, clinicId, onRecordUpdate }) {
  const daily = record.daily_attendance;
  const computed = daily && daily.pct != null ? daily : null;
  const pinned = (record.manual_overrides || []).includes('attendance_pct');
  const days = n => Number(n.toFixed(1));

  const unknown = daily && daily.unknown_program_patients
    ? ` ${daily.unknown_program_patients} patient${daily.unknown_program_patients === 1 ? '' : 's'} left out (program unknown).`
    : '';
  const hint = pinned
    ? `Manually set. Daily attendance syncs will no longer update this value.${computed ? ` Calculated: ${(computed.pct * 100).toFixed(1)}%.` : ''}`
    : computed
      ? `Auto-calculated: ${days(computed.attended_days)} of ${days(computed.expected_days)} expected patient-days attended, ${computed.patients} patient${computed.patients === 1 ? '' : 's'} (${computed.first_date} to ${computed.last_date}).${unknown}`
      : null;

  return (
    <div className="inline-block" title={hint || undefined}>
      <EditableCell value={record.attendance_pct} field="attendance_pct" recordId={record.id} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />
      {pinned ? (
        <span className="block text-[10px] text-amber-600 px-2 leading-tight">Manual</span>
      ) : computed ? (
        <span className="block text-[10px] text-gray-400 px-2 leading-tight">{days(computed.attended_days)} / {days(computed.expected_days)}d</span>
      ) : null}
    </div>
  );
}

function EditableCell({ value, field, recordId, clinicId, onRecordUpdate }) {
  const [editing, setEditing] = useState(false);
  const [displayVal, setDisplayVal] = useState(value);
  const [localValue, setLocalValue] = useState(value ?? '');

  // Keep the displayed value in sync with server truth (e.g. after a recalc
  // reload) so the cell never drifts from what was actually persisted.
  useEffect(() => {
    if (!editing) setDisplayVal(value);
  }, [value, editing]);

  const displayValue = field === 'attendance_pct'
    ? (displayVal != null ? (displayVal * 100).toFixed(2) + '%' : '-')
    : (displayVal != null && displayVal !== '' ? displayVal : '-');

  const handleBlur = async () => {
    setEditing(false);
    let parsed;
    if (field === 'attendance_pct') {
      parsed = parseFloat(localValue) / 100;
      if (isNaN(parsed)) return;
    } else {
      parsed = parseFloat(localValue);
      if (isNaN(parsed)) return;
    }
    if (parsed === displayVal) return;
    setDisplayVal(parsed);
    try {
      const updated = await api.clinics.updateRecord(clinicId, recordId, { [field]: parsed });
      if (onRecordUpdate) onRecordUpdate(recordId, updated);
    } catch (err) {
      console.error('Save failed:', err);
      setDisplayVal(value); // revert on error
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
    if (e.key === 'Escape') { setEditing(false); setLocalValue(value ?? ''); }
  };

  return (
    <div className="inline-block min-w-[60px]">
      {editing ? (
        <input
          type="number"
          step={field === 'attendance_pct' ? '0.01' : '1'}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          style={{ MozAppearance: 'textfield' }}
        />
      ) : (
        <span
          className={`cursor-pointer hover:bg-primary-10 px-2 py-1 rounded transition-colors text-sm block ${
            (field === 'caseload' || field === 'attendance_pct') && (displayVal == null || displayVal === 0) ? 'text-destructive' : ''
          }`}
          onClick={() => {
            setLocalValue(field === 'attendance_pct' && value != null ? (value * 100).toFixed(2) : (value ?? ''));
            setEditing(true);
          }}
        >
          {displayValue}
        </span>
      )}
    </div>
  );
}

/** "Attendance, Length of stay" for a list of missing field keys. */
function fieldList(fields) {
  return fields.map(f => MISSING_FIELD_LABELS[f]).join(', ');
}

/**
 * Per-row flag for a clinician-month with a CScore input missing. The hover
 * names the fields, the same way the score and discharge hovers explain theirs.
 */
function MissingBadge({ gap }) {
  if (!gap) return null;
  return (
    <HoverCard
      width={240}
      panel={(
        <div>
          <h4 className="text-sm font-bold text-text mb-1">Missing this month</h4>
          <p className="text-xs text-gray-600 leading-tight">{fieldList(gap.fields)}</p>
        </div>
      )}
    >
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 whitespace-nowrap">
        <WarningCircle size={12} weight="bold" />
        Missing data
      </span>
    </HoverCard>
  );
}

/**
 * Month heads-up: which included clinicians are missing a CScore
 * input. One line per field, expandable to the names behind it.
 */
function MissingDataBanner({ gaps, clinicId, clinicName }) {
  const [open, setOpen] = useState(false);
  if (gaps.length === 0) return null;

  const counts = Object.keys(MISSING_FIELD_LABELS)
    .map(field => ({ field, count: gaps.filter(g => g.fields.includes(field)).length }))
    .filter(c => c.count > 0);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 text-sm text-amber-800">
          <WarningCircle size={18} weight="bold" className="shrink-0 mt-px" />
          <p>
            {counts.map(({ field, count }, i) => (
              <span key={field}>
                {i > 0 && ' · '}
                <span className="font-medium">{MISSING_FIELD_LABELS[field]}</span> missing for {count} clinician{count === 1 ? '' : 's'}
              </span>
            ))}
          </p>
        </div>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          className="shrink-0 text-xs font-medium text-amber-800 hover:underline"
        >
          {open ? 'Hide' : 'Show clinicians'}
        </button>
      </div>
      {open && (
        <ul className="mt-3 pt-3 border-t border-amber-200 space-y-1.5">
          {gaps.map(gap => (
            <li key={gap.record_id} className="flex flex-wrap items-baseline gap-x-3 text-sm">
              <Link to={`/clinicians/${gap.clinician_id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinicName)}`} className="text-primary font-medium hover:underline">
                {gap.name}
              </Link>
              <span className="text-xs text-amber-800">{fieldList(gap.fields)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Every clinician's metrics rolled up across the selected months.
 *
 * The score in each row is NOT the average of that clinician's monthly
 * CScores — the raw metrics are summed and averaged across the span and then
 * scored once, so a range reads the way a single long reporting period would.
 */
/** "2026-01" -> "January 2026", for headings the client reads. */
function formatMonthLabel(value) {
  const [year, month] = String(value || '').split('-');
  const name = MONTH_NAMES[Number(month)];
  return name ? `${name} ${year}` : value;
}

function RangeTable({ data, loading, clinicId, clinicName, weights, threshold }) {
  if (loading) {
    return <div className="bg-white rounded-xl border border-[#E6E6E6] p-6 text-center text-gray-400 text-sm">Loading range...</div>;
  }
  if (!data || data.clinicians.length === 0) {
    return <div className="bg-white rounded-xl border border-[#E6E6E6] p-6 text-sm text-gray-500">No records in this range.</div>;
  }

  const pct = (v) => (v != null ? `${(Number(v) * 100).toFixed(1)}%` : '-');
  const num = (v, d = 1) => (v != null ? Number(v).toFixed(d) : '-');
  const flagged = data.clinicians.filter(c => c.include_in_cscore && isRedZone(c.cscore, threshold));

  return (
    <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-xl font-bold text-text">
            {data.from === data.to
              ? formatMonthLabel(data.from)
              : `${formatMonthLabel(data.from)} to ${formatMonthLabel(data.to)}`}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {data.month_span} month{data.month_span === 1 ? '' : 's'} · {data.clinicians.length} clinician{data.clinicians.length === 1 ? '' : 's'}
            {flagged.length > 0 && <span className="text-destructive font-medium"> · {flagged.length} in the red zone</span>}
          </p>
        </div>
        {data.avg_cscore != null && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Clinic average</span>
            <ScoreBadge score={data.avg_cscore} size="lg" flagged={isRedZone(data.avg_cscore, threshold)} />
          </div>
        )}
      </div>

      <table className="w-full hidden md:table">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 text-sm font-semibold text-gray-700">Name</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-28">CScore</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-28">Caseload</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-28">Discharges</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-32">Length of Stay</th>
            <th className="text-left py-3 text-sm font-semibold text-gray-700 w-28">Attendance</th>
          </tr>
        </thead>
        <tbody>
          {data.clinicians.map((row) => (
            <tr key={row.clinician_id} className={`border-b border-gray-50 last:border-0 ${row.include_in_cscore ? '' : 'opacity-50'}`}>
              <td className="py-4">
                <Link to={`/clinicians/${row.clinician_id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinicName)}`} className="text-primary font-medium hover:underline text-sm">
                  {row.clinician_name}
                </Link>
                <p className="text-xs text-gray-400 mt-0.5">{row.month_count} month{row.month_count === 1 ? '' : 's'} of data</p>
              </td>
              <td className="py-4">
                {row.cscore == null ? (
                  <span className="text-xs text-gray-400">Pending</span>
                ) : (
                  <ScoreCell score={row.cscore} breakdown={row} weights={weights} threshold={threshold} title="Across this range" />
                )}
              </td>
              <td className="py-4 text-sm">{num(row.caseload)}</td>
              <td className="py-4 text-sm">{num(row.discharges, 0)}</td>
              <td className="py-4 text-sm">{num(row.length_of_stay, 0)}</td>
              <td className="py-4 text-sm">{pct(row.attendance_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="md:hidden space-y-3">
        {data.clinicians.map((row) => (
          <div key={row.clinician_id} className={`border border-[#E6E6E6] rounded-lg p-3 ${row.include_in_cscore ? '' : 'opacity-50'}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <Link to={`/clinicians/${row.clinician_id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinicName)}`} className="text-primary font-medium hover:underline text-sm truncate">
                {row.clinician_name}
              </Link>
              {row.cscore == null
                ? <span className="text-xs text-gray-400 shrink-0">Pending</span>
                : <ScoreCell score={row.cscore} breakdown={row} weights={weights} threshold={threshold} title="Across this range" />}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Caseload</span><span>{num(row.caseload)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Discharges</span><span>{num(row.discharges, 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">LOS</span><span>{num(row.length_of_stay, 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Attendance</span><span>{pct(row.attendance_pct)}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** "YYYY-MM" for a Date, which is what the range endpoints expect. */
function toMonthValue(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Default span: the last six months up to and including this one. */
function defaultRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  return { from: toMonthValue(start), to: toMonthValue(now) };
}

function ClinicView({
  clinic, clinicId, clinicians, chartData, view, setView, range, setRange, rangeData, trendData,
  rangeLoading, rangeError, selectedMonth, selectedYear, onSelectMonth, monthRecords, loadingRecords,
  recalculating, onRecalculate, missingGaps, gapByRecord, onRecordUpdate, onToggleInclude, weights, threshold,
}) {
  const [search, setSearch] = useState('');
  const clinicianLink = (id) => `/clinicians/${id}?clinicId=${clinicId}&clinicName=${encodeURIComponent(clinic.name)}`;
  const shown = clinicians.filter((c) => c.full_name.toLowerCase().includes(search.toLowerCase()));
  const includedCount = clinicians.filter((c) => c.include_in_cscore !== false).length;

  return (
    <div>
      <LargeTitle
        back={{ to: '/clinics', label: 'Clinics' }}
        title={clinic.name}
        subtitle={`${clinicians.length} clinicians · ${includedCount} counted in the CScore`}
      />
      <div className="mb-5">
        <Segmented
          value={view}
          onChange={setView}
          options={[{ value: 'overview', label: 'Overview' }, { value: 'range', label: 'Date range' }, { value: 'trends', label: 'Trends' }]}
        />
      </div>

      {view === 'overview' && (
        <>
          <ScoreHero label="Average CScore" data={chartData} focusMonth={selectedMonth} focusYear={selectedYear} />
          <Section title={selectedMonth ? `${MONTH_NAMES[selectedMonth]} ${selectedYear}` : 'Clinicians'}>
            <MonthChips months={chartData} month={selectedMonth} year={selectedYear} onSelect={onSelectMonth} allLabel="All time" />

            {!selectedMonth && (
              <div className="mt-3">
                {clinicians.length > 6 && <SearchField value={search} onChange={setSearch} placeholder="Search clinicians" />}
                <ListGroup>
                  {shown.length === 0 && <EmptyRow>No clinicians found</EmptyRow>}
                  {shown.map((c) => {
                    const included = c.include_in_cscore !== false;
                    const isActive = c.active !== false;
                    return (
                      <div key={c.id} className="flex items-center gap-3 pl-4 pr-3 md:pl-5 md:pr-5 md:gap-5 min-h-[72px] py-3">
                        <Link to={clinicianLink(c.id)} onClick={tap} className={`flex items-center gap-3.5 flex-1 min-w-0 ${included ? '' : 'opacity-50'}`}>
                          <Monogram name={c.full_name} score={c.avg_cscore} src={c.image_url} />
                          <div className="flex-1 min-w-0">
                            <p className="text-[16px] font-semibold text-[#111] truncate">{c.full_name}</p>
                            <p className="text-[13px] text-gray-500 truncate mt-0.5">
                              {[
                                c.kipu_clinician_id ? `ID ${c.kipu_clinician_id}` : null,
                                !isActive ? 'Inactive' : null,
                                included ? 'Counted' : 'Not counted',
                              ].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                          <ScorePill
                            score={c.avg_cscore}
                            flagged={isRedZone(c.avg_cscore, threshold)}
                            breakdown={c.breakdown}
                            weights={weights}
                            title={`${c.full_name} average`}
                          />
                        </Link>
                        <Switch on={included} onChange={() => onToggleInclude(c)} label={`Count ${c.full_name} in the clinic CScore`} />
                      </div>
                    );
                  })}
                </ListGroup>
                <p className="text-[12px] text-gray-500 px-1 mt-2">The switch sets whether a clinician counts toward this clinic's average.</p>
              </div>
            )}

            {selectedMonth && (
              <div className="mt-3">
                <div className="flex justify-end mb-3">
                  <button
                    type="button"
                    onClick={() => { tap(); onRecalculate(); }}
                    disabled={recalculating}
                    className="h-9 px-4 rounded-full bg-primary-10 text-primary text-[14px] font-semibold disabled:opacity-50"
                  >
                    {recalculating ? 'Recalculating...' : 'Recalculate CScore'}
                  </button>
                </div>
                {loadingRecords ? (
                  <PageSkeleton hero={false} rows={4} />
                ) : monthRecords.length === 0 ? (
                  <Card className="p-6 text-center text-[15px] text-gray-500">No records for this month</Card>
                ) : (
                  <>
                    <MissingDataBanner key={`${selectedYear}-${selectedMonth}`} gaps={missingGaps} clinicId={clinicId} clinicName={clinic.name} />
                    <Card className="hidden md:block px-5 py-2">
                      <table className="w-full table-fixed">
                        <colgroup>
                          <col className="w-[30%]" /><col className="w-[12%]" /><col className="w-[15%]" />
                          <col className="w-[14%]" /><col className="w-[14%]" /><col className="w-[15%]" />
                        </colgroup>
                        <thead>
                          <tr className="border-b border-[#F0EEF5] text-left text-[13px] font-semibold text-gray-500">
                            <th className="py-3">Clinician</th><th className="py-3">CScore</th><th className="py-3">Caseload</th>
                            <th className="py-3">Discharges</th><th className="py-3">Length of stay</th><th className="py-3">Attendance</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F0EEF5]">
                          {monthRecords.map((record) => (
                            <tr key={record.id} className="text-[15px]">
                              <td className="py-4 pr-3">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Link to={clinicianLink(record.clinician_id)} className="font-semibold text-[#111] hover:text-primary">
                                    {record.clinician_name || record.clinicians?.full_name}
                                  </Link>
                                  <MissingBadge gap={gapByRecord.get(record.id)} />
                                </div>
                              </td>
                              <td className="py-4"><RecordScore record={record} weights={weights} threshold={threshold} /></td>
                              <td className="py-4"><CaseloadCell record={record} clinicId={clinicId} onRecordUpdate={onRecordUpdate} /></td>
                              <td className="py-4"><DischargeCell record={record} clinicId={clinicId} onRecordUpdate={onRecordUpdate} /></td>
                              <td className="py-4"><EditableCell value={record.length_of_stay} field="length_of_stay" recordId={record.id} clinicId={clinicId} onRecordUpdate={onRecordUpdate} /></td>
                              <td className="py-4"><AttendanceCell record={record} clinicId={clinicId} onRecordUpdate={onRecordUpdate} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                    <div className="space-y-3 md:hidden">
                      {monthRecords.map((record) => (
                        <Card key={record.id} className="p-4">
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="min-w-0">
                              <Link to={clinicianLink(record.clinician_id)} onClick={tap} className="text-[16px] font-semibold text-[#111] block truncate">
                                {record.clinician_name || record.clinicians?.full_name}
                              </Link>
                              <div className="mt-1"><MissingBadge gap={gapByRecord.get(record.id)} /></div>
                            </div>
                            <RecordScore record={record} weights={weights} threshold={threshold} />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              ['Caseload', <CaseloadCell record={record} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />],
                              ['Discharges', <DischargeCell record={record} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />],
                              ['Length of stay', <EditableCell value={record.length_of_stay} field="length_of_stay" recordId={record.id} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />],
                              ['Attendance', <AttendanceCell record={record} clinicId={clinicId} onRecordUpdate={onRecordUpdate} />],
                            ].map(([label, cell]) => (
                              <div key={label} className="rounded-[14px] bg-[#F6F5FA] px-3 py-2.5">
                                <p className="text-[12px] font-medium text-gray-500 mb-0.5">{label}</p>
                                <div className="text-[16px] font-semibold text-[#111]">{cell}</div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </Section>
        </>
      )}

      {view !== 'overview' && (
        <>
          <Card className="p-4">
            <MonthRangePicker from={range.from} to={range.to} max={toMonthValue(new Date())} disabled={rangeLoading} onChange={setRange} />
            <p className="text-[12px] text-gray-500 mt-3 leading-snug">
              KIPU reports discharges, attendance and length of stay once a month, so ranges are whole months.
            </p>
          </Card>
          {rangeError && <Card className="p-5 mt-3 text-[15px] text-destructive">{rangeError}</Card>}
          {view === 'range' && !rangeError && (
            <div className="mt-3">
              <RangeTable data={rangeData} loading={rangeLoading} clinicId={clinicId} clinicName={clinic.name} weights={weights} threshold={threshold} />
            </div>
          )}
          {view === 'trends' && !rangeError && (
            <Section title="Where each clinician is heading">
              <p className="text-[13px] text-gray-500 px-1 -mt-1 mb-3">First vs last scored month in the range</p>
              <Card className="p-4">
                <TrendPanel series={trendData} clinicId={clinicId} clinicName={clinic.name} loading={rangeLoading} />
              </Card>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

export default function ClinicDetail() {
  const { clinicId } = useParams();
  const { threshold, weights } = useScoring();
  const [view, setView] = useState('overview');
  const [range, setRange] = useState(defaultRange);
  const [rangeData, setRangeData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [monthRecords, setMonthRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.clinics.get(clinicId),
      api.clinics.chart(clinicId),
      api.clinics.clinicians(clinicId),
    ])
      .then(([clinicData, chart, cliniciansList]) => {
        setClinic(clinicData);
        setChartData(chart);
        setClinicians(cliniciansList);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clinicId]);

  // Only the latest request may update the table, so quick month switches
  // can't leave an older month's records on screen
  const latestRecordsRequest = useRef(0);
  const loadMonthRecords = useCallback(() => {
    if (!selectedMonth || !selectedYear) return Promise.resolve();
    const requestId = ++latestRecordsRequest.current;
    setLoadingRecords(true);
    return api.clinics.records(clinicId, selectedMonth, selectedYear)
      .then((records) => { if (requestId === latestRecordsRequest.current) setMonthRecords(records); })
      .catch(console.error)
      .finally(() => { if (requestId === latestRecordsRequest.current) setLoadingRecords(false); });
  }, [clinicId, selectedMonth, selectedYear]);

  useEffect(() => {
    loadMonthRecords();
  }, [loadMonthRecords]);

  // Range and trend share one span, so switching between those two tabs
  // reuses whichever call has already been made for it.
  useEffect(() => {
    if (view === 'overview') return;

    let active = true;
    setRangeLoading(true);
    setRangeError(null);

    const request = view === 'range'
      ? api.clinics.range(clinicId, range.from, range.to).then(data => ({ rangeData: data }))
      : api.clinics.trend(clinicId, range.from, range.to).then(data => ({ trendData: data }));

    request
      .then(result => {
        if (!active) return;
        if (result.rangeData) setRangeData(result.rangeData);
        else setTrendData(result.trendData);
      })
      .catch(err => {
        if (!active) return;
        console.error(err);
        setRangeError('Could not load data for this range.');
      })
      .finally(() => { if (active) setRangeLoading(false); });

    return () => { active = false; };
  }, [view, clinicId, range.from, range.to]);

  // Recomputed from local state, so a typed-in value or an include toggle
  // clears its flag straight away.
  const missingGaps = useMemo(
    () => (selectedMonth && selectedYear
      ? findMissingData(monthRecords, clinicians, { month: selectedMonth, year: selectedYear })
      : []),
    [monthRecords, clinicians, selectedMonth, selectedYear]
  );
  const gapByRecord = useMemo(() => new Map(missingGaps.map(g => [g.record_id, g])), [missingGaps]);

  const handleRecordUpdate = useCallback((recordId, updatedRecord) => {
    setMonthRecords(prev => prev.map(r => r.id === recordId ? { ...r, ...updatedRecord } : r));
  }, []);

  const handleToggleInclude = async (clinician) => {
    const next = !(clinician.include_in_cscore !== false);
    // Optimistically flip the toggle, then recompute the clinic CScore chart.
    setClinicians(prev => prev.map(c => c.id === clinician.id ? { ...c, include_in_cscore: next } : c));
    try {
      await api.clinics.updateClinician(clinicId, clinician.id, { include_in_cscore: next });
      const chart = await api.clinics.chart(clinicId);
      setChartData(chart);
    } catch (err) {
      console.error('Toggle include failed:', err);
      setClinicians(prev => prev.map(c => c.id === clinician.id ? { ...c, include_in_cscore: !next } : c));
    }
  };

  const handlePointClick = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await api.clinics.recalculate(clinicId, selectedMonth, selectedYear);
      // Reload records and chart
      await loadMonthRecords();
      const chart = await api.clinics.chart(clinicId);
      setChartData(chart);
    } catch (err) {
      console.error('Recalculate failed:', err);
    }
    setRecalculating(false);
  };

  if (loading || !clinic) return <PageSkeleton />;

  return (
    <ClinicView
      clinic={clinic} clinicId={clinicId} clinicians={clinicians} chartData={chartData}
      view={view} setView={setView} range={range} setRange={setRange} rangeData={rangeData} trendData={trendData}
      rangeLoading={rangeLoading} rangeError={rangeError}
      selectedMonth={selectedMonth} selectedYear={selectedYear} onSelectMonth={handlePointClick}
      monthRecords={monthRecords} loadingRecords={loadingRecords} recalculating={recalculating} onRecalculate={handleRecalculate}
      missingGaps={missingGaps} gapByRecord={gapByRecord} onRecordUpdate={handleRecordUpdate}
      onToggleInclude={handleToggleInclude} weights={weights} threshold={threshold}
    />
  );
}
